import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import xmarkIcon from "../assets/xmark.svg";
import {
  listAiProviders,
  getAiPreference,
  saveAiPreference,
  getAiUsage,
} from "../api";
import { isProviderReady, isManagedProvider, providerLabel } from "../ai-integration/providers";

const SETTINGS_WIDTH = 320;
const SETTINGS_MAX_HEIGHT = 360;
const ANCHOR_GAP = 8;
const VIEWPORT_PAD = 8;

const initialsFor = (name = "") => {
  const parts = String(name || "").split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (parts.length === 0) return "AI";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const ACCENTS = [
  { bg: "rgba(156, 120, 255, 0.12)", border: "rgba(156, 120, 255, 0.28)", fg: "rgba(196, 174, 255, 0.95)" },
  { bg: "rgba(255, 156, 110, 0.12)", border: "rgba(255, 156, 110, 0.28)", fg: "rgba(255, 196, 160, 0.95)" },
  { bg: "rgba(110, 200, 255, 0.12)", border: "rgba(110, 200, 255, 0.28)", fg: "rgba(170, 220, 255, 0.95)" },
  { bg: "rgba(110, 220, 170, 0.12)", border: "rgba(110, 220, 170, 0.28)", fg: "rgba(170, 235, 200, 0.95)" },
  { bg: "rgba(255, 200, 110, 0.12)", border: "rgba(255, 200, 110, 0.28)", fg: "rgba(255, 220, 160, 0.95)" },
  { bg: "rgba(255, 130, 180, 0.12)", border: "rgba(255, 130, 180, 0.28)", fg: "rgba(255, 180, 210, 0.95)" },
];
const accentFor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return ACCENTS[h % ACCENTS.length];
};

// managedProviderCaption — show the tenant's remaining monthly allowance next
// to the managed provider so switching to it gives balance feedback in place.
const managedProviderCaption = (usage) => {
  if (!usage) return "Managed by Control Seat";
  const remaining = Math.max(0, usage.remaining_microusd) / 1e6;
  return `Managed by Control Seat · $${remaining.toFixed(2)} left this month`;
};

export default function GatewayAIModelSettings({ open, onClose, anchorRef }) {
  const [providers, setProviders] = useState([]);
  const [preference, setPreference] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProviders([]);
    setPreference(null);
    Promise.all([
      listAiProviders(),
      getAiPreference().catch(() => undefined),
      getAiUsage().catch(() => null),
    ])
      .then(([providerData, prefData, usageData]) => {
        if (cancelled) return;
        setProviders(Array.isArray(providerData) ? providerData : []);
        setUsage(usageData && typeof usageData.remaining_microusd === "number" ? usageData : null);
        setPreference(prefData === undefined ? null : (prefData || { configured: false }));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load providers");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const computePosition = () => {
      const anchor = anchorRef?.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const popHeight = popoverRef.current?.offsetHeight || SETTINGS_MAX_HEIGHT;
      const spaceAbove = rect.top - VIEWPORT_PAD;
      const placeAbove = spaceAbove >= popHeight + ANCHOR_GAP;
      const top = placeAbove
        ? Math.max(VIEWPORT_PAD, rect.top - popHeight - ANCHOR_GAP)
        : Math.min(window.innerHeight - VIEWPORT_PAD - popHeight, rect.bottom + ANCHOR_GAP);
      const rawLeft = rect.right - SETTINGS_WIDTH;
      const maxLeft = window.innerWidth - SETTINGS_WIDTH - VIEWPORT_PAD;
      const left = Math.min(maxLeft, Math.max(VIEWPORT_PAD, rawLeft));
      setPosition({ top, left });
    };
    computePosition();
    const raf = window.requestAnimationFrame(computePosition);
    window.addEventListener("resize", computePosition);
    window.addEventListener("scroll", computePosition, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", computePosition);
      window.removeEventListener("scroll", computePosition, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    const handleMouse = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorRef?.current?.contains(event.target)) return;
      onClose?.();
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleMouse);
    document.addEventListener("keydown", handleKey, true);
    return () => {
      document.removeEventListener("mousedown", handleMouse);
      document.removeEventListener("keydown", handleKey, true);
    };
  }, [open, onClose, anchorRef]);

  const handleSelect = async (provider) => {
    if (!isProviderReady(provider)) return;
    if (savingId) return;
    setSavingId(provider.id);
    setError(null);
    try {
      const model = isManagedProvider(provider) ? "" : (provider.default_model || "");
      await saveAiPreference({
        provider_id: provider.id,
        model,
      });
      setPreference({
        configured: true,
        provider_id: provider.id,
        model,
      });
    } catch (err) {
      setError(err?.message || "Failed to save preference");
    } finally {
      setSavingId(null);
    }
  };

  if (!open) return null;

  const visibleProviders = providers.filter((provider) => (
    isProviderReady(provider) || isManagedProvider(provider)
  ));
  const activeId = preference?.configured
    ? (preference.provider_id || "")
    : (preference
      ? (visibleProviders.find((provider) => provider.is_default)?.id || "")
      : "");
  const portalTarget = (typeof document !== "undefined" && document.body) || null;
  if (!portalTarget) return null;

  const node = (
    <div
      ref={popoverRef}
      className="gwAISettings"
      role="dialog"
      aria-label="AI model settings"
      onMouseDown={(e) => e.stopPropagation()}
      style={{ top: position.top, left: position.left }}
    >
      <div className="gwAISettingsHeader">
        <div className="gwAISettingsHeaderText">
          <div className="gwAISettingsTitle">AI Model</div>
          <div className="gwAISettingsSubtitle">
            Pick which model the assistant uses
          </div>
        </div>
        <button
          type="button"
          className="gwAISettingsClose"
          onClick={onClose}
          aria-label="Close settings"
        >
          <img src={xmarkIcon} alt="" />
        </button>
      </div>

      <div className="gwAISettingsBody">
        {loading && <div className="gwAISettingsState">Loading providers…</div>}

        {!loading && visibleProviders.length === 0 && !error && (
          <div className="gwAISettingsEmpty">
            <div className="gwAISettingsEmptyTitle">No providers configured</div>
            <div className="gwAISettingsEmptyText">
              An admin needs to configure an AI provider before you can switch
              models.
            </div>
            <a href="/api-ai-settings" className="gwAISettingsLink">
              Open AI settings →
            </a>
          </div>
        )}

        {!loading && visibleProviders.length > 0 && (
          <ul className="gwAISettingsList">
            {visibleProviders.map((provider) => {
              const isActive = activeId === provider.id;
              const isSaving = savingId === provider.id;
              const ready = isProviderReady(provider);
              const label = providerLabel(provider);
              const accent = accentFor(label || provider.id);
              return (
                <li key={provider.id}>
                  <button
                    type="button"
                    className={`gwAISettingsItem${isActive ? " is-active" : ""}`}
                    onClick={() => handleSelect(provider)}
                    disabled={isSaving || !ready}
                    aria-pressed={isActive}
                  >
                    <span
                      className="gwAISettingsAvatar"
                      style={{
                        background: accent.bg,
                        borderColor: accent.border,
                        color: accent.fg,
                      }}
                      aria-hidden="true"
                    >
                      {initialsFor(label)}
                    </span>
                    <span className="gwAISettingsItemBody">
                      <span className="gwAISettingsItemTop">
                        <span className="gwAISettingsName">{label}</span>
                        {provider.is_default && (
                          <span className="gwAISettingsBadge">Default</span>
                        )}
                      </span>
                      <span className="gwAISettingsModel">
                        {isManagedProvider(provider)
                          ? (ready ? managedProviderCaption(usage) : "Unavailable")
                          : (provider.default_model || "no model set")}
                      </span>
                    </span>
                    <span className="gwAISettingsCheck" aria-hidden="true">
                      {isSaving ? (
                        <span className="gwAISettingsSpinner" />
                      ) : isActive ? (
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M3 8.5L6.5 12L13 4.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {error && <div className="gwAISettingsError">{error}</div>}
      </div>

      <div className="gwAISettingsFooter">
        <a href="/api-ai-settings" className="gwAISettingsLink">
          Manage providers →
        </a>
      </div>
    </div>
  );

  return createPortal(node, portalTarget);
}
